import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { RichTextCopyButton } from '@/components/ui/RichTextCopyButton'
import { convertMarkdownToDocx, convertMarkdownToPdf } from '@/lib/docx-utils'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import DOMPurify from 'dompurify'
import { Copy, Download, FileMinus, FileText, Link, RefreshCw, Send, X } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { seoArticleUpdater } from 'sprites-types/workflow/tools/seoArticleUpdater'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { DiffVisualization } from './DiffVisualization'
import { InternalLinksDisplay } from './InternalLinksDisplay'

// Function to safely sanitize HTML content
function sanitizeHtml(htmlContent: string): string {
  return DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'p',
      'br',
      'strong',
      'em',
      'u',
      'a',
      'ul',
      'ol',
      'li',
      'blockquote',
      'img',
      'figure',
      'figcaption',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'pre',
      'code',
      'div',
      'span',
    ],
    ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'title', 'width', 'height', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
  })
}

// Block content extraction utilities
function createSummaryMarkdown(
  originalSlug: string,
  sourceDomain: string | undefined,
  targetKeywords: string[],
  updatedAt: string,
  wordCount: number,
  articleDocxUrl?: string,
  generatedImages?: any[]
): string {
  return `## ✨ SEO Article Updated

**Article:** ${originalSlug}
${sourceDomain ? `**Source:** ${sourceDomain}` : ''}
**Updated:** ${new Date(updatedAt).toLocaleString()}

### 📊 Article Overview

| Metric | Value |
|--------|-------|
| 📖 **Current Word Count** | ${wordCount.toLocaleString()} |
| 🎯 **Target Keywords** | ${targetKeywords.length} |
${generatedImages && generatedImages.length > 0 ? `| 🎨 **AI Images Generated** | ${generatedImages.length} |` : ''}
${articleDocxUrl ? `| 📄 **Updated DOCX** | [Download DOCX](${articleDocxUrl}) |` : ''}

${
  targetKeywords.length > 0
    ? `### 🔍 Target Keywords

${targetKeywords
  .map((keyword, index) => {
    const emoji = index === 0 ? '🎯' : index === 1 ? '🎪' : index === 2 ? '🎨' : '⭐'
    return `${emoji} **${keyword}**`
  })
  .join('\n\n')}`
    : ''
}`
}

function createChangesMarkdown(
  changesMade: number,
  seoImprovement: string | undefined,
  wordCountChange: string | undefined,
  sectionsModified: number,
  sectionsAdded: number,
  sectionsRemoved: number,
  changes: any[] = [],
  seoUpdates: any = {},
  generatedImages?: any[]
): string {
  let markdown = `## 🚀 Changes Summary

### 📈 Improvements Made

| Change Type | Count |
|-------------|-------|
| ✏️ **Total Changes** | ${changesMade} improvements |
${sectionsModified > 0 ? `| 🔄 **Sections Modified** | ${sectionsModified} |` : ''}
${sectionsAdded > 0 ? `| ➕ **Sections Added** | ${sectionsAdded} |` : ''}
${sectionsRemoved > 0 ? `| ➖ **Sections Removed** | ${sectionsRemoved} |` : ''}

${
  seoImprovement || wordCountChange
    ? `### 📊 Performance Impact

${seoImprovement ? `📈 **SEO Score Improvement:** ${seoImprovement}` : ''}
${wordCountChange ? `📝 **Word Count Change:** ${wordCountChange}` : ''}`
    : ''
}`

  // Add SEO Updates section
  if (seoUpdates && (seoUpdates.metaTitle || seoUpdates.metaDescription || seoUpdates.keywords)) {
    markdown += `\n\n### 🎯 SEO Updates\n`

    if (seoUpdates.metaTitle && seoUpdates.metaTitle.status === 'modified') {
      markdown += `\n**Meta Title Changes:**\n\n| Field | Content |\n|-------|--------|\n`
      if (seoUpdates.metaTitle.oldValue) markdown += `| OLD | ${seoUpdates.metaTitle.oldValue} |\n`
      if (seoUpdates.metaTitle.newValue)
        markdown += `| NEW | **${seoUpdates.metaTitle.newValue}** |\n`
    }

    if (seoUpdates.metaDescription && seoUpdates.metaDescription.status === 'modified') {
      markdown += `\n**Meta Description Changes:**\n\n| Field | Content |\n|-------|--------|\n`
      if (seoUpdates.metaDescription.oldValue)
        markdown += `| OLD | ${seoUpdates.metaDescription.oldValue} |\n`
      if (seoUpdates.metaDescription.newValue)
        markdown += `| NEW | ${seoUpdates.metaDescription.newValue} |\n`
    }

    if (seoUpdates.keywords && seoUpdates.keywords.status === 'modified') {
      markdown += `\n**Keywords Changes:**\n\n| Field | Keywords |\n|-------|----------|\n`
      if (seoUpdates.keywords.removed && seoUpdates.keywords.removed.length > 0) {
        markdown += `| REMOVED | ${seoUpdates.keywords.removed.join(', ')} |\n`
      }
      if (seoUpdates.keywords.added && seoUpdates.keywords.added.length > 0) {
        markdown += `| ADDED | ${seoUpdates.keywords.added.join(', ')} |\n`
      }
    }
  }

  // Add detailed content changes
  if (changes && changes.length > 0) {
    markdown += `\n\n### 📝 Content Changes\n`

    changes.forEach((change, index) => {
      // Map changeType to display format
      const changeTypeMap: Record<string, { emoji: string; text: string }> = {
        replace: { emoji: '✏️', text: 'REPLACED' },
        insert_after: { emoji: '➕', text: 'INSERTED AFTER' },
        insert_before: { emoji: '➕', text: 'INSERTED BEFORE' },
        delete: { emoji: '➖', text: 'DELETED' },
      }

      const changeInfo = changeTypeMap[change.changeType || 'replace'] || {
        emoji: '✏️',
        text: 'MODIFIED',
      }
      const statusEmoji = changeInfo.emoji
      const statusText = changeInfo.text

      markdown += `\n**${statusEmoji} Change ${index + 1}: ${statusText}**\n\n`
      markdown += `| Field | Content |\n|-------|--------|\n`
      markdown += `| Action | ${statusText} |\n`

      if (change.changeReason) {
        markdown += `| Reason | ${change.changeReason} |\n`
      }

      if (change.keywordsTargeted && change.keywordsTargeted.length > 0) {
        markdown += `| Keywords | ${change.keywordsTargeted.join(', ')} |\n`
      }

      if (change.findText) {
        markdown += `| Find Text | ${change.findText.substring(0, 100)}${change.findText.length > 100 ? '...' : ''} |\n`
      }

      if (change.replaceWith) {
        markdown += `| Replace With | ${change.replaceWith.substring(0, 100)}${change.replaceWith.length > 100 ? '...' : ''} |\n`
      }

      if (change.contextBefore) {
        markdown += `| Context Before | ${change.contextBefore.substring(0, 50)}... |\n`
      }

      if (change.contextAfter) {
        markdown += `| Context After | ${change.contextAfter.substring(0, 50)}... |\n`
      }
    })
  }

  // Internal links are now handled by the dedicated InternalLinksDisplay component

  // Add Generated Images section
  if (generatedImages && generatedImages.length > 0) {
    markdown += `\n\n### 🎨 Generated Images\n\nCreated **${generatedImages.length} AI image${generatedImages.length > 1 ? 's' : ''}** to enhance the updated article:\n\n`

    generatedImages.forEach((image: any, _index: number) => {
      markdown += `![${image.alt}](${image.url})\n`
      markdown += `*${image.placement}* | Style: ${image.style || 'Default'}\n`
      markdown += `Prompt: "${image.prompt}"\n\n`
    })
  }

  return markdown
}

function generateAnchorUrl(sectionId: string): string {
  return `${window.location.href.split('#')[0]}#${sectionId}`
}

// Function to extract article URL from inputs or metadata
function extractArticleUrl(nodeExecution: any, metadata: any): string {
  // First try to get from inputs.contentInput if it looks like a URL
  const contentInput = nodeExecution.inputs?.contentInput
  if (contentInput && typeof contentInput === 'string') {
    try {
      new URL(contentInput)
      return contentInput
    } catch {
      // Not a URL, continue to other methods
    }
  }

  // Try metadata.sourceUrl
  if (metadata?.sourceUrl) {
    return metadata.sourceUrl
  }

  // Fallback to constructing URL from domain and slug
  if (metadata?.sourceDomain && metadata?.originalSlug) {
    const domain = metadata.sourceDomain.replace(/^https?:\/\//, '')
    return `https://${domain}/${metadata.originalSlug}`
  }

  // Final fallback
  return metadata?.originalSlug || 'unknown-article'
}

// Function to clean article content and remove metadata sections
function cleanArticleContent(content: string): string {
  if (!content || typeof content !== 'string') {
    console.warn('cleanArticleContent: Invalid content provided')
    return ''
  }

  // Log original content length for debugging
  console.log(`cleanArticleContent: Processing ${content.length} characters`)

  // First, try to find content before any metadata sections
  const metadataSplitRegex =
    /\n\s*(SEO Updates|Meta Title Changes|Content Changes|Updated with \d+ improvements|Article Update Report|Executive Summary|Changes Summary|🚀 Changes Summary)/i
  const beforeMetadata = content.split(metadataSplitRegex)[0]

  if (beforeMetadata && beforeMetadata.trim() && beforeMetadata.trim() !== content.trim()) {
    const cleaned = beforeMetadata.trim()
    console.log(`cleanArticleContent: Found content before metadata (${cleaned.length} chars)`)
    return cleaned
  }

  // If that doesn't work, use line-by-line filtering with more comprehensive patterns
  const lines = content.split('\n')
  const cleanLines: string[] = []
  let inMetadataSection = false
  let foundContentStart = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    const originalLine = lines[i]

    // Skip common metadata section headers and everything after
    if (
      line.match(
        /^(SEO Updates|Meta Title Changes|Meta Description Changes|Keywords Changes|Content Changes|Updated with \d+ improvements|Article Update Report|Executive Summary|Changes Summary|🚀 Changes Summary|## ✨ SEO Article Updated)/i
      )
    ) {
      console.log(`cleanArticleContent: Found metadata section at line ${i + 1}: ${line}`)
      inMetadataSection = true
      continue
    }

    // Skip table patterns and metadata content
    if (
      inMetadataSection ||
      line.match(/^Field\s*(\||\s+)(Content|Keywords)$/i) ||
      line.match(/^\|?\s*-+\s*\|?\s*-+\s*\|?$/) ||
      line.match(/^\|\s*(OLD|NEW|REMOVED|ADDED|Action|Section|Impact|Reason|Metric|Value)\s*\|/i) ||
      line.match(/^(OLD|NEW|REMOVED|ADDED|Action|Section|Impact|Reason)\s+/i) ||
      line.match(/^➕ Change \d+:/i) ||
      line.match(/^Field\s+(Content|Keywords)$/i) ||
      line.match(
        /^\*\*Article:\*\*|^\*\*Source:\*\*|^\*\*Updated:\*\*|^\*\*Current Word Count\*\*|^\*\*Target Keywords\*\*/
      ) ||
      line.match(/^📖|^🎯|^📄|^🔍|^⭐|^🎪|^🎨/) || // Skip emoji lines from summary
      line.match(
        /^#+\s*(Article Overview|Target Keywords|Changes Made|Performance Impact|SEO Updates)/
      ) ||
      line.match(/^-+$/) // Skip separator lines
    ) {
      continue
    }

    // Keep track if we've found actual content
    if (!foundContentStart && line.length > 10 && !line.match(/^#{1,6}\s/)) {
      foundContentStart = true
      console.log(`cleanArticleContent: Found content start at line ${i + 1}`)
    }

    // Keep the line if it looks like actual article content
    if (originalLine.trim() || foundContentStart) {
      cleanLines.push(originalLine)
    }
  }

  // Join lines and clean up excessive whitespace
  const result = cleanLines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^[\n\s]+/, '') // Remove leading whitespace
    .trim()

  console.log(`cleanArticleContent: Cleaned content length: ${result.length} characters`)

  // Additional validation - if result is too short compared to original, it might be over-cleaned
  if (result.length < content.length * 0.1 && content.length > 500) {
    console.warn(
      `cleanArticleContent: Result seems too short (${result.length} vs ${content.length}), returning less aggressive clean`
    )

    // Fall back to less aggressive cleaning - just remove obvious metadata
    const lessAggressiveResult = content
      .split(/\n\s*(?:SEO Updates|Meta Title Changes|Article Update Report)/i)[0]
      .replace(/\|\s*(OLD|NEW|REMOVED|ADDED)\s*\|.*\|/g, '') // Remove table rows
      .replace(/^\|?\s*-+\s*\|?\s*-+\s*\|?$/gm, '') // Remove table separators
      .replace(/\n{3,}/g, '\n\n')
      .trim()

    return lessAggressiveResult
  }

  return result
}

function SeoArticleUpdaterDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(seoArticleUpdater, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)
  const { sendMessage } = useCopilotContext()
  const [isPublishing, setIsPublishing] = useState(false)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [rerollingImages, setRerollingImages] = useState<Set<number>>(new Set())
  const [publishForm, setPublishForm] = useState({
    targetType: 'blog' as 'blog' | 'page',
    blogHandle: 'news',
    articleId: '',
    pageId: '',
    published: true,
  })

  // Get result data (may be undefined if parsing failed)
  const result = parseResult.success ? parseResult.data : undefined

  // Image re-roll handler
  const handleRerollImage = useCallback(
    async (imageIndex: number, originalPrompt: string, imageStyle?: string) => {
      if (!parseResult.success || !result?.generatedImages) return

      setRerollingImages((prev) => new Set(prev).add(imageIndex))

      try {
        const response = await fetch('/api/v1/seo/regenerate-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            originalPrompt,
            imageStyle,
            keywords: result!.metadata.targetKeywords || [],
            articleContent: result!.changes
              ?.map((c) => c.replaceWith)
              .join('\n\n')
              .slice(0, 1000),
          }),
        })

        const data = await response.json()

        if (data.success && data.imageUrl) {
          // Update the image URL in the result
          if (result!.generatedImages && result!.generatedImages[imageIndex]) {
            result!.generatedImages[imageIndex].url = data.imageUrl
            result!.generatedImages[imageIndex].prompt = data.imagePrompt || originalPrompt
          }

          toast.success('Image regenerated successfully!')

          setRerollingImages((prev) => {
            const newSet = new Set(prev)
            newSet.delete(imageIndex)
            return newSet
          })
        } else {
          throw new Error(data.error || 'Failed to regenerate image')
        }
      } catch (error) {
        console.error('Failed to regenerate image:', error)
        toast.error('Failed to regenerate image. Please try again.')
        setRerollingImages((prev) => {
          const newSet = new Set(prev)
          newSet.delete(imageIndex)
          return newSet
        })
      }
    },
    [parseResult, result]
  )

  // Action creators - must be at top level before any early returns
  const createBlockActions = useCallback(
    (
      content: string,
      blockName: string,
      sectionId: string,
      originalSlug: string
    ): ContextAction[] => {
      return [
        {
          id: `copy-${sectionId}`,
          label: `Copy ${blockName}`,
          icon: <Copy className="h-4 w-4" />,
          onClick: () => {
            navigator.clipboard
              .writeText(content)
              .then(() => toast.success(`${blockName} copied to clipboard`))
              .catch(() => toast.error(`Failed to copy ${blockName}`))
          },
        },
        {
          id: `download-docx-${sectionId}`,
          label: `Download ${blockName} as DOCX`,
          icon: <Download className="h-4 w-4" />,
          onClick: async () => {
            try {
              const filename = `seo-${sectionId}-${originalSlug}-${Date.now()}.docx`
              const blob = await convertMarkdownToDocx(content)
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = filename
              link.click()
              URL.revokeObjectURL(url)
              toast.success(`${blockName} DOCX downloaded`)
            } catch (error) {
              console.error('Error creating DOCX:', error)
              toast.error(`Failed to download ${blockName} DOCX`)
            }
          },
        },
        {
          id: `download-pdf-${sectionId}`,
          label: `Download ${blockName} as PDF`,
          icon: <FileText className="h-4 w-4" />,
          onClick: async () => {
            try {
              const filename = `seo-${sectionId}-${originalSlug}-${Date.now()}.pdf`
              const blob = await convertMarkdownToPdf(content)
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = filename
              link.click()
              URL.revokeObjectURL(url)
              toast.success(`${blockName} PDF downloaded`)
            } catch (error) {
              console.error('Error creating PDF:', error)
              toast.error(`Failed to download ${blockName} PDF`)
            }
          },
        },
        {
          id: `copy-link-${sectionId}`,
          label: `Copy link to ${blockName}`,
          icon: <Link className="h-4 w-4" />,
          onClick: () => {
            const anchorUrl = generateAnchorUrl(sectionId)
            navigator.clipboard
              .writeText(anchorUrl)
              .then(() => toast.success(`Link to ${blockName} copied to clipboard`))
              .catch(() => toast.error(`Failed to copy link to ${blockName}`))
          },
        },
      ]
    },
    []
  )

  const createArticleActions = useCallback(
    (
      finalArticle: string,
      articleDocxUrl: string | undefined,
      originalSlug: string,
      createBlockActions: any
    ): ContextAction[] => {
      const content =
        finalArticle || '## No Article Content\n\nNo updated article content available.'
      const baseActions = createBlockActions(content, 'Article', 'article', originalSlug)

      // Add direct DOCX download if URL is available
      if (articleDocxUrl) {
        const docxDownloadAction: ContextAction = {
          id: 'download-direct-docx',
          label: 'Download DOCX',
          icon: <FileMinus className="h-4 w-4" />,
          onClick: () => {
            const link = document.createElement('a')
            link.href = articleDocxUrl
            link.download = `${originalSlug}-article-${Date.now()}.docx`
            link.target = '_blank'
            link.click()
            toast.success('Article DOCX downloaded')
          },
        }
        return [docxDownloadAction, ...baseActions]
      }

      return baseActions
    },
    []
  )

  // Publish handlers
  const handlePublishAll = useCallback(() => {
    if (!parseResult.success) return
    setShowPublishModal(true)
  }, [parseResult])

  const handleDirectPublish = useCallback(async () => {
    if (!parseResult.success) return

    setIsPublishing(true)
    try {
      const response = await fetch('/api/v2/seo-tools/publish-to-shopify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          nodeExecutionId: nodeExecution.nodeId,
          targetType: publishForm.targetType,
          blogHandle: publishForm.targetType === 'blog' ? publishForm.blogHandle : undefined,
          articleId:
            publishForm.targetType === 'blog' && publishForm.articleId
              ? publishForm.articleId
              : undefined,
          pageId:
            publishForm.targetType === 'page' && publishForm.pageId
              ? publishForm.pageId
              : undefined,
          published: publishForm.published,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to publish')
      }

      toast.success(
        `Successfully ${publishForm.articleId || publishForm.pageId ? 'updated' : 'created'} ${publishForm.targetType}: ${data.data.title || 'Untitled'}`
      )

      if (data.data.url) {
        toast.success(
          <div>
            <span>View published content: </span>
            <a
              href={data.data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              {data.data.url}
            </a>
          </div>,
          { autoClose: 8000 }
        )
      }

      setShowPublishModal(false)
    } catch (error) {
      console.error('Failed to publish to Shopify:', error)
      toast.error(`Failed to publish: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsPublishing(false)
    }
  }, [parseResult, nodeExecution.nodeId, publishForm])

  const handlePublishChange = useCallback(
    async (changeDescription: string) => {
      if (!parseResult.success) return

      try {
        const result = parseResult.data
        const articleUrl = extractArticleUrl(nodeExecution, result!.metadata)
        const message = `I want to publish this change ${changeDescription} to ${articleUrl}`

        await sendMessage(message)
        toast.success('Publish request sent successfully!')
      } catch (error) {
        console.error('Failed to send publish message:', error)
        toast.error('Failed to send publish request')
      }
    },
    [parseResult, nodeExecution, sendMessage]
  )

  // Early return if parsing failed - after all hooks
  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  // Use new structured schema - result is now guaranteed to be defined
  const summary = result!.summary || {}
  const changesMade = summary.changesMade || 0
  const seoImprovement = summary.seoScoreImprovement || ''
  const wordCountChange = summary.wordCountChange || ''
  const sectionsModified = summary.sectionsModified || 0
  const sectionsAdded = summary.sectionsAdded || 0
  const sectionsRemoved = summary.sectionsRemoved || 0

  // Get context from metadata
  const sourceDomain = result!.metadata.sourceDomain
  const targetKeywords = result!.metadata.targetKeywords || []
  const originalSlug = result!.metadata.originalSlug
  const updatedAt = result!.metadata.updatedAt

  // Get content from new structured schema - prioritize HTML output
  const changesContent = result!.changesMarkdown || ''
  const articleHtml = result!.outputs?.html
  const rawFinalArticle = result!.articleMarkdown || ''
  const finalArticle = cleanArticleContent(rawFinalArticle)

  // Check if we have HTML content available
  const hasArticleHtml = !!articleHtml && articleHtml.trim().length > 0
  const articleDocxUrl = result!.articleDocxUrl
  const wordCount = finalArticle.split(/\s+/).filter(Boolean).length || 0

  // Debug logging and validation
  if (typeof window !== 'undefined' && window.localStorage?.getItem('debug-seo-updater')) {
    console.log('SEO Updater Debug:', {
      hasChangesContent: !!changesContent,
      hasRawArticle: !!rawFinalArticle,
      hasCleanedArticle: !!finalArticle,
      hasArticleDocxUrl: !!articleDocxUrl,
      changesCount: result!.changes?.length || 0,
      seoUpdatesKeys: Object.keys(result!.seoUpdates || {}),
      wordCount,
      rawArticleLength: rawFinalArticle.length,
      cleanedArticleLength: finalArticle.length,
      articlePreview: finalArticle.substring(0, 200),
    })
  }

  // Enhanced validation - check if article processing failed
  const isArticleValid =
    finalArticle &&
    finalArticle.length > 50 &&
    !finalArticle.includes('No updated article content available')
  const processingIssues: string[] = []

  if (!rawFinalArticle || rawFinalArticle.length < 50) {
    processingIssues.push('Raw article content is missing or too short')
  }
  if (rawFinalArticle && !isArticleValid) {
    processingIssues.push('Article merging may have failed - cleaned content is too short')
  }
  if (result!.changes?.length > 0 && !isArticleValid) {
    processingIssues.push(
      `${result!.changes.length} changes were provided but final article seems incomplete`
    )
  }

  if (processingIssues.length > 0) {
    console.warn('SEO Article Updater Processing Issues:', processingIssues)
  }

  // Create block-specific markdown content
  const summaryMarkdown = createSummaryMarkdown(
    originalSlug,
    sourceDomain ?? undefined,
    targetKeywords,
    updatedAt,
    wordCount,
    articleDocxUrl ?? undefined,
    result!.generatedImages
  )

  // Use changes markdown from result if available, otherwise create from summary data
  const changesMarkdown =
    changesContent ||
    createChangesMarkdown(
      changesMade,
      seoImprovement,
      wordCountChange,
      sectionsModified,
      sectionsAdded,
      sectionsRemoved,
      result!.changes || [],
      result!.seoUpdates || {},
      result!.generatedImages
    )

  const summaryActions = createBlockActions(summaryMarkdown, 'Summary', 'summary', originalSlug)
  // Keep using markdown for export compatibility, but display with visual component
  const changesActions = createBlockActions(changesMarkdown, 'Changes', 'changes', originalSlug)
  const articleActions = createArticleActions(
    finalArticle,
    articleDocxUrl ?? undefined,
    originalSlug,
    createBlockActions
  )

  return (
    <div className="space-y-8">
      {/* Action Buttons */}
      <div className="flex items-center justify-between rounded-2xl border border-blue-200/50 bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
        <RichTextCopyButton
          markdown={finalArticle}
          html={hasArticleHtml ? articleHtml : undefined}
          buttonText="Copy as Rich Text"
          size="md"
        />
        <button
          onClick={handlePublishAll}
          disabled={isPublishing}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
        >
          <Send className={`h-4 w-4 ${isPublishing ? 'animate-pulse' : ''}`} />
          {isPublishing ? 'Publishing...' : 'Publish all changes'}
        </button>
      </div>

      {/* Summary Block */}
      <ContextMenuWrapper actions={summaryActions}>
        <div
          id="summary"
          className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/80 text-black shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
        >
          <div className="px-6 py-5">
            <EnhancedMarkdown
              className="prose prose-slate prose-sm max-w-none"
              style={{ backgroundColor: 'transparent', color: '#1e293b', lineHeight: '1.7' }}
            >
              {summaryMarkdown}
            </EnhancedMarkdown>
          </div>
        </div>
      </ContextMenuWrapper>

      {/* Changes Block */}
      <ContextMenuWrapper actions={changesActions}>
        <div
          id="changes"
          className="rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50/80 to-teal-50/80 text-black shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
        >
          <div className="px-6 py-5">
            <div className="mb-6">
              <h2 className="mb-2 text-xl font-bold text-emerald-800">🚀 Changes Summary</h2>
              <div className="rounded-lg bg-emerald-100/50 px-3 py-2 text-sm text-emerald-700">
                {changesMade} improvement{changesMade !== 1 ? 's' : ''} made to optimize the article
              </div>
            </div>
            <DiffVisualization
              changes={result!.changes || []}
              seoUpdates={result!.seoUpdates || {}}
              onPublishChange={handlePublishChange}
              articleUrl={extractArticleUrl(nodeExecution, result!.metadata)}
            />
          </div>
        </div>
      </ContextMenuWrapper>

      {/* Internal Links Block */}
      {result!.internalLinks &&
        result!.internalLinks.linksAdded &&
        result!.internalLinks.linksAdded.length > 0 && (
          <div
            id="internal-links"
            className="rounded-2xl border border-purple-200/60 bg-gradient-to-br from-purple-50/80 to-indigo-50/80 text-black shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
          >
            <div className="px-6 py-5">
              <InternalLinksDisplay
                internalLinks={result!.internalLinks}
                onPublishLinks={handlePublishChange}
                articleUrl={extractArticleUrl(nodeExecution, result!.metadata)}
              />
            </div>
          </div>
        )}

      {/* Generated Images Block */}
      {result!.generatedImages && result!.generatedImages.length > 0 && (
        <div
          id="generated-images"
          className="rounded-2xl border border-purple-200/60 bg-gradient-to-br from-purple-50/80 to-indigo-50/80 text-black shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
        >
          <div className="px-6 py-5">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-purple-500"></div>
              <h3 className="text-lg font-medium text-purple-900">
                🎨 Generated Images ({result!.generatedImages.length})
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {result!.generatedImages.map((image: any, index: number) => (
                <div
                  key={index}
                  className="rounded-xl border border-white/50 bg-white/70 p-4 backdrop-blur-sm"
                >
                  <div className="space-y-3">
                    <div className="relative">
                      <Image
                        src={image.url}
                        alt={image.alt}
                        width={400}
                        height={160}
                        className="h-40 w-full rounded-lg object-cover"
                      />
                      <div className="absolute right-2 top-2">
                        <button
                          onClick={() => handleRerollImage(index, image.prompt, image.style)}
                          disabled={rerollingImages.has(index)}
                          className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-xs font-medium text-purple-700 shadow-sm backdrop-blur-sm transition-colors hover:bg-white disabled:opacity-50"
                        >
                          <RefreshCw
                            className={`h-3 w-3 ${rerollingImages.has(index) ? 'animate-spin' : ''}`}
                          />
                          {rerollingImages.has(index) ? 'Generating...' : 'Re-roll'}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium text-gray-700">Placement:</span>
                        <span className="ml-1 text-gray-600">{image.placement}</span>
                      </div>
                      {image.style && (
                        <div>
                          <span className="font-medium text-gray-700">Style:</span>
                          <span className="ml-1 text-gray-600">{image.style}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-700">Prompt:</span>
                        <span className="ml-1 text-gray-600">{image.prompt}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Full Article Block */}
      <ContextMenuWrapper actions={articleActions}>
        <div
          id="article"
          className="rounded-2xl border border-blue-200/60 bg-gradient-to-br from-blue-50/60 to-sky-50/60 text-black shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
        >
          <div className="px-6 py-6">
            <div className="mb-6">
              <h2 className="mb-3 text-2xl font-bold text-blue-800">📄 Updated Article</h2>
              <div className="rounded-lg bg-blue-100/50 px-4 py-2 text-sm text-blue-700">
                Optimized article content ready for publishing
              </div>
            </div>
            {isArticleValid ? (
              <div className="rounded-xl border border-blue-100 bg-white/80 p-6 shadow-inner">
                {hasArticleHtml && articleHtml ? (
                  // Render HTML directly to preserve links, CTAs, and images
                  <div
                    className="prose prose-slate prose-base max-w-none"
                    style={{ backgroundColor: 'transparent', color: '#1e293b', lineHeight: '1.8' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(articleHtml) }}
                  />
                ) : (
                  // Fallback to markdown rendering
                  <EnhancedMarkdown
                    className="prose prose-slate prose-base max-w-none"
                    style={{ backgroundColor: 'transparent', color: '#1e293b', lineHeight: '1.8' }}
                  >
                    {finalArticle}
                  </EnhancedMarkdown>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-amber-300/60 bg-gradient-to-br from-amber-50/80 to-orange-50/80 p-6 shadow-lg">
                  <div className="mb-4 text-4xl">⚠️</div>
                  <div className="mb-2 text-lg font-bold text-amber-800">
                    Article Processing Issue
                  </div>
                  <div className="space-y-2 text-sm text-amber-800">
                    <div>
                      The final article content appears to be incomplete or failed to merge
                      properly.
                    </div>
                    {processingIssues.length > 0 && (
                      <div>
                        <div className="mb-1 font-medium">Issues detected:</div>
                        <ul className="list-inside list-disc space-y-1 text-xs">
                          {processingIssues.map((issue, index) => (
                            <li key={index}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {rawFinalArticle && rawFinalArticle.length > 50 && (
                  <div className="rounded-2xl border border-blue-300/60 bg-gradient-to-br from-blue-50/80 to-cyan-50/80 p-6 shadow-lg">
                    <div className="mb-2 text-sm font-bold text-blue-800">
                      📄 Raw Article Content (Debug View)
                    </div>
                    <div className="mb-3 text-xs text-blue-800">
                      This is the unprocessed content from the backend. It may contain metadata.
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <EnhancedMarkdown
                        className="prose prose-xs max-w-none text-blue-900"
                        style={{ backgroundColor: 'transparent' }}
                      >
                        {rawFinalArticle.substring(0, 5000) +
                          (rawFinalArticle.length > 5000 ? '\n\n... (truncated for display)' : '')}
                      </EnhancedMarkdown>
                    </div>
                  </div>
                )}

                <div className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-slate-50/80 to-gray-50/80 p-6 text-center shadow-lg">
                  <div className="text-sm text-slate-700">
                    ✅ Changes and SEO improvements are documented in the sections above
                    <br />
                    💡 Enable debug mode:{' '}
                    <code className="rounded bg-gray-200 px-1">
                      localStorage.setItem(&apos;debug-seo-updater&apos;, &apos;true&apos;)
                    </code>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ContextMenuWrapper>

      {/* Publish Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Publish to Shopify</h3>
              <button
                onClick={() => setShowPublishModal(false)}
                className="text-gray-400 transition-colors hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Target Type */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Content Type</label>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setPublishForm({ ...publishForm, targetType: 'blog' })}
                    className={`flex-1 rounded-lg border px-4 py-2 transition-all ${
                      publishForm.targetType === 'blog'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Blog Article
                  </button>
                  <button
                    onClick={() => setPublishForm({ ...publishForm, targetType: 'page' })}
                    className={`flex-1 rounded-lg border px-4 py-2 transition-all ${
                      publishForm.targetType === 'page'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Page
                  </button>
                </div>
              </div>

              {/* Blog Handle (for blog type) */}
              {publishForm.targetType === 'blog' && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Blog Handle
                  </label>
                  <input
                    type="text"
                    value={publishForm.blogHandle}
                    onChange={(e) => setPublishForm({ ...publishForm, blogHandle: e.target.value })}
                    placeholder="e.g., news, blog"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The Shopify blog handle (e.g., &lsquo;news&rsquo; for /blogs/news/)
                  </p>
                </div>
              )}

              {/* Update existing content */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {publishForm.targetType === 'blog'
                    ? 'Update Existing Article (Optional)'
                    : 'Update Existing Page (Optional)'}
                </label>
                <input
                  type="text"
                  value={
                    publishForm.targetType === 'blog' ? publishForm.articleId : publishForm.pageId
                  }
                  onChange={(e) =>
                    setPublishForm({
                      ...publishForm,
                      ...(publishForm.targetType === 'blog'
                        ? { articleId: e.target.value }
                        : { pageId: e.target.value }),
                    })
                  }
                  placeholder={`Enter ${publishForm.targetType === 'blog' ? 'article' : 'page'} ID to update`}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to create new {publishForm.targetType}
                </p>
              </div>

              {/* Publish Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="published"
                  checked={publishForm.published}
                  onChange={(e) => setPublishForm({ ...publishForm, published: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="published" className="ml-2 block text-sm text-gray-900">
                  Publish immediately (uncheck to save as draft)
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowPublishModal(false)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDirectPublish}
                disabled={
                  isPublishing || (publishForm.targetType === 'blog' && !publishForm.blogHandle)
                }
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className={`h-4 w-4 ${isPublishing ? 'animate-pulse' : ''}`} />
                {isPublishing
                  ? 'Publishing...'
                  : `${publishForm.articleId || publishForm.pageId ? 'Update' : 'Create'} ${
                      publishForm.targetType === 'blog' ? 'Article' : 'Page'
                    }`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function downloadSeoArticleUpdater(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function seoArticleUpdaterToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(seoArticleUpdater, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  // Use new structured schema for markdown
  const summary = result.summary || {}
  const changesMade = summary.changesMade || 0
  const seoImprovement = summary.seoScoreImprovement || ''
  const wordCountChange = summary.wordCountChange || ''
  const sectionsModified = summary.sectionsModified || 0
  const sectionsAdded = summary.sectionsAdded || 0
  const sectionsRemoved = summary.sectionsRemoved || 0

  // Get context from metadata
  const sourceDomain = result!.metadata.sourceDomain
  const targetKeywords = result!.metadata.targetKeywords || []
  const originalSlug = result!.metadata.originalSlug
  const updatedAt = result!.metadata.updatedAt

  // Get content from new structured schema (same as UI) - prioritize HTML
  const changesContent = result!.changesMarkdown || ''
  const articleHtml = result!.outputs?.html
  const rawFinalArticle = result!.articleMarkdown || ''
  const finalArticle = cleanArticleContent(rawFinalArticle)

  // Check if we have HTML content available for export
  const hasArticleHtml = !!articleHtml && articleHtml.trim().length > 0
  const articleDocxUrl = result!.articleDocxUrl
  const wordCount = finalArticle.split(/\s+/).filter(Boolean).length || 0

  // Create block-specific markdown content (same as UI)
  const summaryMarkdown = createSummaryMarkdown(
    originalSlug,
    sourceDomain ?? undefined,
    targetKeywords,
    updatedAt,
    wordCount,
    articleDocxUrl ?? undefined,
    result!.generatedImages
  )

  // Use changes markdown from result if available, otherwise create from summary data
  const changesMarkdown =
    changesContent ||
    createChangesMarkdown(
      changesMade,
      seoImprovement,
      wordCountChange,
      sectionsModified,
      sectionsAdded,
      sectionsRemoved,
      result!.changes || [],
      result!.seoUpdates || {},
      result!.generatedImages
    )

  // Combine all blocks
  let markdown = summaryMarkdown + '\n\n---\n\n'
  markdown += changesMarkdown + '\n\n---\n\n'
  markdown += '## 📄 Updated Article\n\n'

  // Prioritize HTML content for export if available
  const exportContent =
    hasArticleHtml && articleHtml
      ? articleHtml
      : typeof finalArticle === 'string' && finalArticle
        ? finalArticle
        : 'No updated article content available'

  markdown += exportContent
  markdown += '\n\n---\n'

  return markdown
}

export const SeoArticleUpdaterDisplay = createWorkflowToolComponent(
  SeoArticleUpdaterDisplayComponent,
  downloadSeoArticleUpdater,
  seoArticleUpdaterToMarkdown
)
